import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { reportsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Alert, Empty, ErrorNote, Loading, PageHeader, Toolbar } from '@/components/common/Ui'
import { dateOnly, money } from '@/lib/format'
import { saveBlob } from '@/lib/download'
import { periodRange } from '@/lib/periods'

// Hidden, not removed: the Excel export. The API still serves it
// (format=xlsx); flip this back to true to offer it again.
const SHOW_EXCEL = false

const TABS = [
  ['product', 'Products'],
  ['customer', 'Customers'],
  ['category', 'Categories'],
]

const qty = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n.toLocaleString('en-IN', { maximumFractionDigits: 3 }) : '—'
}

function show(value, type) {
  if (value === null || value === undefined || value === '') return '—'
  if (type === 'money') return money(value)
  if (type === 'qty') return qty(value)
  if (type === 'date') return dateOnly(value)
  return value
}

const numeric = (type) => ['money', 'qty', 'int'].includes(type)

/**
 * What sold, for a period: by product (how much of each, its weight, how
 * many orders and customers), by customer (with what each bought), and by
 * category. Sold means accepted and not cancelled — confirmed, packed,
 * dispatched or delivered — as on the dashboard. Opens on this week.
 */
export default function SalesReports() {
  const [kind, setKind] = useState('product')
  const [period, setPeriod] = useState('this_week')
  const [custom, setCustom] = useState({ date_from: '', date_to: '' })
  const [open, setOpen] = useState(() => new Set())
  const [downloading, setDownloading] = useState('')
  const [error, setError] = useState('')

  const range = period === 'custom' ? custom : periodRange(period)
  const params = Object.fromEntries(Object.entries(range).filter(([, v]) => v))

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['sales-by', kind, params],
    queryFn: () => reportsApi.salesBy(kind, params),
  })

  const download = async (format) => {
    setError('')
    setDownloading(format)
    try {
      const { blob, filename } = await reportsApi.exportSalesBy(kind, params, format)
      saveBlob(blob, filename)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setDownloading('')
    }
  }

  // The customer breakdown is a column of its own in the file; on screen it
  // is the rows that open under each customer.
  const columns = (data?.columns ?? []).filter((c) => c.type !== 'detail')
  const totals = data?.totals

  const toggle = (id) =>
    setOpen((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="sales-page">
      <PageHeader
        title="Sales Reports"
        subtitle="Confirmed, packed, dispatched and delivered orders"
        actions={
          <select
            aria-label="Export this report"
            className="export-select"
            value=""
            disabled={Boolean(downloading) || !data?.rows?.length}
            onChange={(e) => {
              const format = e.target.value
              if (format) download(format)
            }}
          >
            <option value="">{downloading ? 'Preparing…' : 'Export'}</option>
            <option value="csv">CSV</option>
            {SHOW_EXCEL && <option value="xlsx">Excel</option>}
            <option value="pdf">PDF</option>
          </select>
        }
      />

      <nav className="tabs" aria-label="Report">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`tab${kind === key ? ' active' : ''}`}
            onClick={() => {
              setKind(key)
              setOpen(new Set())
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      <Toolbar>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period">
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="this_week">This week</option>
          <option value="last_week">Last week</option>
          <option value="this_month">This month</option>
          <option value="last_month">Last month</option>
          <option value="custom">Custom dates</option>
        </select>
        {period === 'custom' && (
          <>
            <label className="loads-date">
              <span>From</span>
              <input
                type="date"
                value={custom.date_from}
                onChange={(e) => setCustom((c) => ({ ...c, date_from: e.target.value }))}
              />
            </label>
            <label className="loads-date">
              <span>To</span>
              <input
                type="date"
                value={custom.date_to}
                onChange={(e) => setCustom((c) => ({ ...c, date_to: e.target.value }))}
              />
            </label>
          </>
        )}
        {range.date_from && period !== 'custom' && (
          <span className="muted sales-period">
            {dateOnly(range.date_from)}
            {range.date_to !== range.date_from && ` – ${dateOnly(range.date_to)}`}
          </span>
        )}
      </Toolbar>

      <Alert tone="danger" message={error} onClose={() => setError('')} />

      {isLoading && <Loading />}
      {isError && <ErrorNote message="Could not load the report" onRetry={refetch} />}

      {totals && (
        <div className="sales-totals">
          <div><span className="muted">Orders</span><strong>{totals.orders}</strong></div>
          <div><span className="muted">Customers</span><strong>{totals.customers}</strong></div>
          <div><span className="muted">Products sold</span><strong>{totals.products}</strong></div>
          <div><span className="muted">Total weight</span><strong>{qty(totals.weight_kg)} kg</strong></div>
          <div><span className="muted">GST</span><strong>{money(totals.tax)}</strong></div>
          <div><span className="muted">Total sales</span><strong>{money(totals.amount)}</strong></div>
        </div>
      )}

      {data && !data.rows.length && (
        <Empty title="Nothing sold in this period" hint="Try another period." />
      )}

      {data && data.rows.length > 0 && (
        <div className="loads-table-wrap">
          <table className="table sales-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={numeric(c.type) ? 'right' : undefined}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, index) => {
                const id = row.product_id ?? row.customer_id ?? row.category ?? index
                const expandable = kind === 'customer' && row.items?.length > 0
                return (
                  <Fragment key={id}>
                    <tr
                      className={expandable ? 'sales-expandable' : undefined}
                      onClick={expandable ? () => toggle(id) : undefined}
                    >
                      {columns.map((c, i) => (
                        <td key={c.key} className={numeric(c.type) ? 'right nowrap' : undefined}>
                          {i === 0 && expandable && (
                            <span className="sales-caret">{open.has(id) ? '▾' : '▸'} </span>
                          )}
                          {show(row[c.key], c.type)}
                          {c.key === 'quantity' && row.unit ? ` ${row.unit}` : ''}
                        </td>
                      ))}
                    </tr>
                    {expandable &&
                      open.has(id) &&
                      row.items.map((item) => (
                        <tr key={`${id}-${item.code}`} className="sales-detail">
                          <td colSpan={columns.length}>
                            <span>{item.product}</span>
                            <span className="muted"> · {item.code}</span>
                            <span className="sales-detail-figures">
                              {qty(item.quantity)} {item.unit} · {item.orders} orders ·{' '}
                              {money(item.amount)}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="sales-total-row">
                {columns.map((c, i) => (
                  <td key={c.key} className={numeric(c.type) ? 'right nowrap' : undefined}>
                    {i === 0
                      ? 'Total'
                      : ['weight_kg', 'taxable', 'tax', 'amount'].includes(c.key)
                        ? show(totals[c.key], c.type)
                        : ['orders', 'customers', 'products'].includes(c.key) && c.key in totals
                          ? totals[c.key]
                          : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
